import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/ui";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
  FaLightbulb,
  FaRocket,
  FaChevronRight,
  FaMobileAlt,
  FaStore,
  FaUserTie,
  FaGraduationCap,
  FaHospital,
  FaUtensils,
  FaHome,
  FaCog,
  FaCodeBranch,
  FaRobot,
  FaBullhorn,
  FaUsers,
  FaFileExcel,
  FaCommentDots,
} from "react-icons/fa";

const scenarios = [
  {
    icon: FaStore,
    title: "E-commerce & Retail",
    color: "from-orange-500 to-amber-600",
    automations: [
      "Auto-reply to product inquiries with catalog links",
      "Send order confirmation and shipping updates via Flow Builder",
      "Run flash sale campaigns to segmented customer lists",
      "Recover abandoned carts with timed follow-up sequences",
    ],
  },
  {
    icon: FaUserTie,
    title: "Sales & Lead Generation",
    color: "from-blue-500 to-indigo-600",
    automations: [
      "Cold outreach campaigns with smart delays to avoid bans",
      "Qualify leads with AI chatbot before handing to a rep",
      "Move leads through CRM pipeline stages automatically",
      "Follow up with prospects who haven't replied in 48 hours",
    ],
  },
  {
    icon: FaGraduationCap,
    title: "Education & Coaching",
    color: "from-violet-500 to-purple-600",
    automations: [
      "Drip-feed course content over days using Campaigns",
      "Answer student FAQs instantly with an AI knowledge base",
      "Send class reminders and assignment notifications",
      "Collect feedback via WhatsApp and tag responses in CRM",
    ],
  },
  {
    icon: FaHospital,
    title: "Healthcare & Clinics",
    color: "from-teal-500 to-cyan-600",
    automations: [
      "Appointment reminders with reschedule options",
      "Post-visit follow-ups and feedback collection",
      "Broadcast health tips to patient groups",
      "Verify patient phone numbers before sending updates",
    ],
  },
  {
    icon: FaUtensils,
    title: "Restaurants & Food Delivery",
    color: "from-red-500 to-rose-600",
    automations: [
      "Auto-share menu and daily specials on inquiry",
      "Order confirmation and delivery tracking messages",
      "Loyalty campaigns targeting repeat customers",
      "Collect reviews after delivery with automated flows",
    ],
  },
  {
    icon: FaHome,
    title: "Real Estate & Services",
    color: "from-emerald-500 to-green-600",
    automations: [
      "Send property listings to leads based on their preferences",
      "Schedule site visit reminders and follow-ups",
      "Nurture cold leads with weekly market updates via Campaigns",
      "Export group members from community groups for targeted outreach",
    ],
  },
];

const features = [
  {
    icon: FaCodeBranch,
    title: "Flow Builder",
    slug: "flow-builder",
    desc: "Visual drag-and-drop builder for automated WhatsApp workflows. Set triggers, delays, conditions, and loops.",
  },
  {
    icon: FaRobot,
    title: "AI Chatbot & Voice",
    slug: "ai-features",
    desc: "Connect OpenAI for intelligent auto-replies. Train with your own knowledge base. Text-to-voice with ElevenLabs.",
  },
  {
    icon: FaBullhorn,
    title: "Campaigns",
    slug: "campaigns",
    desc: "Multi-step outreach sequences across sessions. Target by tags or pipeline stage. Edit live campaigns.",
  },
  {
    icon: FaUsers,
    title: "CRM & Leads",
    slug: "crm",
    desc: "Pipeline view, custom fields, tags, import/export. Track every conversation and lead stage.",
  },
  {
    icon: FaFileExcel,
    title: "Send via Excel",
    slug: "send-via-excel",
    desc: "Upload a spreadsheet with numbers and messages. Walytic sends them with randomized delays.",
  },
  {
    icon: FaCommentDots,
    title: "WhatsApp Widget",
    slug: "whatsapp-widget",
    desc: "Floating chat button for your website. Captures leads straight into your Walytic CRM.",
  },
];

const steps = [
  {
    num: "1",
    title: "Connect your WhatsApp",
    desc: "Go to WhatsApp Phones, click Add Phone, and scan the QR code with your WhatsApp app. Your number is live in under 30 seconds.",
  },
  {
    num: "2",
    title: "Import or add contacts",
    desc: "Head to CRM and import contacts from Excel, or add them one by one. Tag them by interest, source, or stage so you can target them later.",
  },
  {
    num: "3",
    title: "Build your first automation",
    desc: "Open Flow Builder and create a flow. Pick a trigger (incoming message, keyword, or schedule), add message nodes, and publish. Your bot is live.",
  },
  {
    num: "4",
    title: "Launch a campaign",
    desc: "Go to Campaigns, select your audience by tags, write your sequence, set timing, and hit Start. Walytic sends messages across sessions with smart delays.",
  },
  {
    num: "5",
    title: "Monitor and iterate",
    desc: "Check your Unibox for live conversations. Review CRM pipeline for lead progress. Edit flows and campaigns anytime without downtime.",
  },
];

export default function GettingStartedGuide() {
  useEffect(() => {
    document.title = "Getting Started with Walytic | User Guide";
  }, []);

  return (
    <div className="page-content">
      <PageHeader
        title="Getting Started with Walytic"
        subtitle="Learn how Walytic automates WhatsApp communication for your business -- from first setup to advanced workflows."
      />

      <div className="max-w-4xl mx-auto">
        {/* Back link */}
        <Link
          to="/guide"
          className="inline-flex items-center text-sm text-brand hover:underline mb-8"
        >
          <FaArrowLeft className="mr-2 h-3 w-3" /> Back to all guides
        </Link>

        {/* Table of Contents */}
        <nav className="bg-surface-card rounded-xl border border-surface-border p-6 mb-12">
          <h2 className="font-semibold text-lg text-ink-primary mb-4">
            In this guide
          </h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { id: "what-is-walytic", label: "What is Walytic?" },
              { id: "quick-start", label: "Quick start in 5 steps" },
              { id: "features-overview", label: "Features at a glance" },
              { id: "use-cases", label: "Automation scenarios by industry" },
              { id: "tips", label: "Tips for getting the most out of Walytic" },
            ].map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="flex items-center text-sm text-ink-secondary hover:text-brand py-1.5 transition-colors"
              >
                <FaChevronRight className="mr-2 h-3 w-3 text-ink-tertiary" />
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* What is Walytic */}
        <section id="what-is-walytic" className="mb-12">
          <h2 className="text-2xl font-bold text-ink-primary mb-4">
            What is Walytic?
          </h2>
          <div className="bg-surface-card rounded-xl border border-surface-border p-6">
            <p className="text-ink-secondary leading-relaxed mb-4">
              Walytic is a WhatsApp automation platform that helps businesses
              send messages, manage leads, and build chatbots -- all from one
              dashboard. Instead of manually messaging contacts one by one, you
              set up workflows that handle it automatically.
            </p>
            <p className="text-ink-secondary leading-relaxed mb-4">
              Whether you need to run cold outreach campaigns, auto-reply to
              customer questions with AI, or track leads through a sales
              pipeline, Walytic gives you the tools to do it without writing
              code.
            </p>
            <div className="bg-blue-50 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-3">
                <FaLightbulb className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm text-ink-secondary">
                  <strong className="text-ink-primary">Think of it this way:</strong>{" "}
                  Walytic is your WhatsApp operations center. Connect your number,
                  import your contacts, set your rules, and let the platform handle
                  the repetitive work.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Start */}
        <section id="quick-start" className="mb-12">
          <h2 className="text-2xl font-bold text-ink-primary mb-4">
            Quick start in 5 steps
          </h2>
          <div className="space-y-4">
            {steps.map((step) => (
              <div
                key={step.num}
                className="bg-surface-card rounded-xl border border-surface-border p-6 flex gap-5"
              >
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-brand to-blue-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                  {step.num}
                </div>
                <div>
                  <h3 className="font-semibold text-ink-primary mb-1">
                    {step.title}
                  </h3>
                  <p className="text-ink-secondary text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features Overview */}
        <section id="features-overview" className="mb-12">
          <h2 className="text-2xl font-bold text-ink-primary mb-4">
            Features at a glance
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <Link
                  key={f.slug}
                  to={`/guide/${f.slug}`}
                  className="group bg-surface-card rounded-xl border border-surface-border p-5 hover:border-brand/30 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="h-5 w-5 text-brand" />
                    <h3 className="font-semibold text-ink-primary group-hover:text-brand transition-colors">
                      {f.title}
                    </h3>
                  </div>
                  <p className="text-ink-secondary text-sm leading-relaxed">
                    {f.desc}
                  </p>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Use Cases by Industry */}
        <section id="use-cases" className="mb-12">
          <h2 className="text-2xl font-bold text-ink-primary mb-4">
            Automation scenarios by industry
          </h2>
          <p className="text-ink-secondary mb-6">
            Here is how different businesses use Walytic to save time and reach
            more people.
          </p>
          <div className="space-y-6">
            {scenarios.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="bg-surface-card rounded-xl border border-surface-border p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`h-10 w-10 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center`}
                    >
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg text-ink-primary">
                      {s.title}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {s.automations.map((a, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-ink-secondary">
                        <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        {/* Tips */}
        <section id="tips" className="mb-12">
          <h2 className="text-2xl font-bold text-ink-primary mb-4">
            Tips for getting the most out of Walytic
          </h2>
          <div className="bg-surface-card rounded-xl border border-surface-border p-6">
            <ul className="space-y-4">
              {[
                {
                  title: "Start with one flow, then expand",
                  desc: "Build a simple auto-reply flow first. Once you see it working, add conditions, delays, and AI nodes.",
                },
                {
                  title: "Tag your contacts early",
                  desc: "Tags are how you target campaigns and filter leads. The sooner you organize contacts, the more precise your outreach gets.",
                },
                {
                  title: "Use smart delays in outreach",
                  desc: "Sending 500 messages in 10 seconds gets your number flagged. Campaigns and Send via Excel both add randomized delays to keep you safe.",
                },
                {
                  title: "Verify numbers before bulk sends",
                  desc: "Run your list through Number Verification first. Messaging inactive numbers wastes quota and hurts delivery rates.",
                },
                {
                  title: "Train your AI chatbot with real FAQs",
                  desc: "Upload your actual support questions as a knowledge base. The more specific the training data, the better the AI replies.",
                },
                {
                  title: "Check Unibox daily",
                  desc: "Unibox shows all conversations across connected numbers in one place. Catch hot leads before they go cold.",
                },
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-3">
                  <FaRocket className="h-4 w-4 text-brand mt-1 shrink-0" />
                  <div>
                    <strong className="text-ink-primary text-sm">{tip.title}</strong>
                    <p className="text-ink-secondary text-sm mt-0.5">{tip.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Next steps */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 text-center border border-blue-100 mb-8">
          <FaRocket className="h-10 w-10 text-brand mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-ink-primary mb-3">
            Ready to build?
          </h3>
          <p className="text-ink-secondary mb-6">
            Pick a feature guide below and follow the step-by-step instructions.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/guide/flow-builder"
              className="inline-flex items-center px-5 py-2.5 bg-brand hover:opacity-90 text-white font-semibold rounded-xl shadow transition-all text-sm"
            >
              Flow Builder <FaArrowRight className="ml-2 h-3 w-3" />
            </Link>
            <Link
              to="/guide/campaigns"
              className="inline-flex items-center px-5 py-2.5 bg-white hover:bg-gray-50 text-ink-primary font-semibold rounded-xl shadow border border-surface-border transition-all text-sm"
            >
              Campaigns <FaArrowRight className="ml-2 h-3 w-3" />
            </Link>
            <Link
              to="/guide/crm"
              className="inline-flex items-center px-5 py-2.5 bg-white hover:bg-gray-50 text-ink-primary font-semibold rounded-xl shadow border border-surface-border transition-all text-sm"
            >
              CRM <FaArrowRight className="ml-2 h-3 w-3" />
            </Link>
          </div>
        </div>

        {/* Back link */}
        <Link
          to="/guide"
          className="inline-flex items-center text-sm text-brand hover:underline"
        >
          <FaArrowLeft className="mr-2 h-3 w-3" /> Back to all guides
        </Link>
      </div>
    </div>
  );
}
